import { Suspense, lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";

// layouts
import DashboardLayout from "../layouts/dashboard";
import AuthLayout from "../layouts/auth/AuthLayout"; // Import the new AuthLayout

// config
import { DEFAULT_PATH } from "../config";
import LoadingScreen from "../components/LoadingScreen";

const Loadable = (Component, { fallback = <LoadingScreen /> } = {}) => (props) => {
  return (
    <Suspense fallback={fallback}> 
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: '/auth',
      element: <AuthLayout/>, // Use AuthLayout here
      children:[
        {element: <LoginPage/>, path:'login'},
        {element: <RegisterPage/>, path:'register'},
        {element: <ResetPasswordPage/>, path:'reset-password'},
        {element: <NewPasswordPage/>, path:'new-password'},
      ]
    },
    {
      path: "/nonappchat", // New route for non-app chat
      element: <NonAppChatPage />,
    },
    {
      path: "/loginlink", // New route for auto-login
      element: <LoginLinkPage />,
    },
    {
      path: "/external-schedule", // Moved outside DashboardLayout
      element: <ExternalSchedulePage />,
    },
    {
      path: "/",
      element: <DashboardLayout />,
      children: [
        { element: <Navigate to={DEFAULT_PATH} replace />, index: true },
        { path: "app", element: <GeneralApp /> },
        { path: "settings", element: <Settings /> },
        { path: "group", element: <GroupPage /> },
        { path: "pager", element: <PagerPage /> }, // New pager route
        { path: "call", element: <CallPage /> },
        { path: "advanced-search", element: <AdvanceSearchPage /> },
        { path: "profile", element: <ProfilePage /> },
        { path: "broadcast-messages", element: <BroadcastMessagesPage /> }, 
        { path: "resources", element: <ResourcesPage /> }, // New resources route
        { path: "settings/chat-appearance", element: <ChatAppearanceSettingsPage /> }, // New chat appearance settings path
        { path: "settings/change-password", element: <ChangePasswordPage /> }, // New change password settings path
        { path: "settings/adjust-font-size", element: <AdjustFontSizeSettingsPage /> }, // New adjust font size settings path
        { path: "settings/app-configuration", element: <AppConfigurationSettingsPage /> }, // New app configuration settings path
        { path: "settings/department-schedule", element: <DepartmentSchedulePage /> }, // New department schedule path
        { path: "404", element: <Page404 /> },
        { path: "*", element: <Navigate to="/404" replace /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
  ]);
}

const GeneralApp = Loadable(
  lazy(() => import("../pages/dashboard/GeneralApp")),
  { fallback: null },
);

const LoginPage = Loadable(
  lazy(() => import("../pages/auth/Login")),
);

const RegisterPage = Loadable(
  lazy(() => import("../pages/auth/Register")),
);

const ResetPasswordPage = Loadable(
  lazy(() => import("../pages/auth/ResetPassword")),
);

const NewPasswordPage = Loadable(
  lazy(() => import("../pages/auth/NewPassword")),
);

const GroupPage = Loadable(
  lazy(() => import("../pages/dashboard/Group")),
);

const PagerPage = Loadable(
  lazy(() => import("../pages/dashboard/Pager")), // New Pager component
);

const Settings = Loadable(
  lazy(() => import("../pages/dashboard/Settings")),
);

const CallPage = Loadable(
  lazy(() => import("../pages/dashboard/Call")),
);

const AdvanceSearchPage = Loadable(
  lazy(() => import("../pages/dashboard/AdvanceSearch")),
);

const ProfilePage = Loadable(
  lazy(() => import("../pages/dashboard/Profile")),
);

const BroadcastMessagesPage = Loadable(
  lazy(() => import("../pages/dashboard/BroadcastMessagesPage.tsx")), // Updated import path
);

const ResourcesPage = Loadable(
  lazy(() => import("../pages/dashboard/ResourcesPage.tsx")), // New ResourcesPage import
);

const ChatAppearanceSettingsPage = Loadable(
  lazy(() => import("../pages/dashboard/ChatAppearanceSettings.js")), // New ChatAppearanceSettings import
);

const ChangePasswordPage = Loadable(
  lazy(() => import("../pages/dashboard/ChangePasswordPage.js")), // New ChangePasswordPage import
);

const AdjustFontSizeSettingsPage = Loadable(
  lazy(() => import("../pages/dashboard/AdjustFontSizeSettings.js")), // New AdjustFontSizeSettingsPage import
);

const AppConfigurationSettingsPage = Loadable(
  lazy(() => import("../pages/dashboard/AppConfigurationSettings.js")), // New AppConfigurationSettingsPage import
);

const DepartmentSchedulePage = Loadable(
  lazy(() => import("../pages/dashboard/DepartmentSchedulePage.tsx")), // New DepartmentSchedulePage import
);

const ExternalSchedulePage = Loadable(
  lazy(() => import("../pages/dashboard/ExternalSchedulePage.js")), // New ExternalSchedulePage import
);

const NonAppChatPage = Loadable(
  lazy(() => import("../pages/NonAppChatPage.js")), // New NonAppChatPage import
);

const LoginLinkPage = Loadable(
  lazy(() => import("../pages/auth/LoginLinkPage.js")), // New LoginLinkPage import
);

const Page404 = Loadable(lazy(() => import("../pages/Page404")));